import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  billingService,
  type BillingStats,
  type BillingStatsParams,
  type BillingCase,
  type BillingCasesParams,
  type BillingSubmitResponse,
  type MarkManualEntryRequest,
  type MarkManualEntryResponse,
} from "../services";
import type { PaginatedResponse } from "../services/cases.service";

export function useBillingStats(params: BillingStatsParams = {}) {
  return useQuery<BillingStats>({
    queryKey: ["billing-stats", params],
    queryFn: () => billingService.getStats(params),
    refetchInterval: 60000,
  });
}

export function useBillingCases(params: BillingCasesParams = {}) {
  return useQuery<PaginatedResponse<BillingCase>>({
    queryKey: ["billing-cases", params],
    queryFn: () => billingService.getCasesReadyForBilling(params),
  });
}

export function useBillingConfiguration() {
  return useQuery({
    queryKey: ["billing-configuration"],
    queryFn: () => billingService.getConfiguration(),
    staleTime: 30000,
    retry: 1,
  });
}

export function useSubmitToTebra() {
  const queryClient = useQueryClient();

  return useMutation<BillingSubmitResponse, Error, string>({
    mutationFn: (caseId: string) => billingService.submitToTebra(caseId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["billing-stats"] });
      queryClient.invalidateQueries({ queryKey: ["billing-cases"] });
      notifications.show({
        title: "Success",
        message: data.message || "Case submitted to Tebra successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      queryClient.invalidateQueries({ queryKey: ["billing-cases"] });
      const message =
        error?.response?.data?.message ||
        error.message ||
        "Failed to submit case";
      notifications.show({
        title: "Submission Failed",
        message,
        color: "red",
      });
    },
  });
}

export function useRetrySubmission() {
  const queryClient = useQueryClient();

  return useMutation<BillingSubmitResponse, Error, string>({
    mutationFn: (caseId: string) => billingService.retrySubmission(caseId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["billing-stats"] });
      queryClient.invalidateQueries({ queryKey: ["billing-cases"] });
      notifications.show({
        title: "Success",
        message: data.message || "Case resubmitted to Tebra successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      queryClient.invalidateQueries({ queryKey: ["billing-cases"] });
      const message =
        error?.response?.data?.message ||
        error.message ||
        "Failed to retry submission";
      notifications.show({
        title: "Retry Failed",
        message,
        color: "red",
      });
    },
  });
}

export function useMarkManualEntry() {
  const queryClient = useQueryClient();

  return useMutation<
    MarkManualEntryResponse,
    Error,
    { caseId: string; data: MarkManualEntryRequest }
  >({
    mutationFn: ({ caseId, data }) =>
      billingService.markManuallyEntered(caseId, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["billing-stats"] });
      queryClient.invalidateQueries({ queryKey: ["billing-cases"] });
      queryClient.invalidateQueries({
        queryKey: ["reports", "manual-tebra-entry"],
      });
      queryClient.invalidateQueries({
        queryKey: ["reports", "billing-status"],
      });
      notifications.show({
        title: "Manual entry recorded",
        message:
          result.message ||
          "The Tebra encounter was recorded and will not be submitted automatically.",
        color: "green",
      });
    },
    onError: (error: any) => {
      const serverMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message;
      const isStalePacket =
        error?.response?.status === 409 &&
        /(packet|report version|changed after|refresh)/i.test(
          String(serverMessage || "")
        );
      notifications.show({
        title: "Could not record manual entry",
        message:
          (isStalePacket
            ? "This case changed after the worksheet was loaded. Refresh it, reconcile the new signed packet with Tebra, and try again."
            : serverMessage) || "Please verify the identifiers and try again.",
        color: "red",
      });
    },
  });
}
